import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, cwave, envelope, inCubic, inOut, inQuad, keys, lerp, outCubic, outExpo, outQuad, phase, settle, smooth, span, wave } from '../curves.ts';
import { apply, timeline, type Ch, type KeyPose } from '../pose.ts';

/*
 * Wyvern de ceniza (models/wyvern.ts): the era II flyer that hangs over a tower and spits fire down on it. It
 * hovers at its rest height and every clip keeps it in the air except `die`, which drops it to the ground.
 *
 * Wings are authored for the right one (+X) and mirrored: on `arm` +Z lifts it, +Y sweeps it back, +X turns the
 * leading edge down; on `fore` −Y folds the forearm in, on `hand` +Y folds the fingers back; Z on both flexes
 * the outer wing up (+) or down (−). +X pitches the body, the neck and the head nose down, opens the jaw, lifts
 * the tail and swings the legs back. `sac` (the throat sac) is scaled: it swells before a spit and empties as it
 * spits. `spit` is a tongue of fire built tiny at the mouth: scaled by `JET` it shows at full length.
 *
 * Weight: a heavy, slow beat (a crow beats three times while it beats once), a deep driving downstroke with the
 * wing spread and the body heaving up on it, then an upstroke with the wrist leading and the wing half folded.
 * The neck holds the head steady while the trunk heaves; the whip of the tail trails every move.
 */

type B =
  | 'root' | 'body' | 'neck1' | 'neck2' | 'head' | 'jaw' | 'sac' | 'spit'
  | 'armL' | 'foreL' | 'handL' | 'armR' | 'foreR' | 'handR'
  | 'tail1' | 'tail2' | 'tail3' | 'tail4' | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

const sin = Math.sin;
/** How far the tongue of fire is scaled up to show at full length (models/wyvern.ts `SPIT_REST` = 0.025). */
const JET = 40;
/** The ground under the hovering origin (models/wyvern.ts `WYVERN_HOVER`), less the body lying on its side. */
const GROUND = -0.97;

const mirror = (c: Ch): Ch => [c[0], -c[1], -c[2], -(c[3] ?? 0), c[4] ?? 0, c[5] ?? 0, c[6] ?? 1];
/** Both wings from the right one's arm, forearm and hand. */
const wings = (arm: Ch, fore: Ch = [0, 0, 0], hand: Ch = [0, 0, 0]): P => ({
  armR: arm, foreR: fore, handR: hand, armL: mirror(arm), foreL: mirror(fore), handL: mirror(hand),
});
/** A neck curl shared down the chain (heavier at the base); the head on its own. */
const neck = (x: number, head: number, y = 0): P => ({ neck1: [x * 0.55, y * 0.55, 0], neck2: [x * 0.45, y * 0.45, 0], head: [head, y * 0.3, 0] });
/** A tail curl growing toward the tip: +x lifts it, +y swings it to the left (−X). */
const tail = (x: number, y = 0): P => ({ tail1: [x * 0.7, y * 0.7, 0], tail2: [x * 0.9, y * 0.9, 0], tail3: [x * 1.1, y * 1.1, 0], tail4: [x * 1.3, y * 1.3, 0] });
const sum = (...ps: P[]): P => {
  const out: Partial<Record<B, number[]>> = {};
  for (const p of ps) {
    for (const [k, c] of Object.entries(p) as [B, Ch][]) {
      const cur = out[k] ?? [0, 0, 0, 0, 0, 0, 1];
      for (let i = 0; i < 7; i++) {
        const v = c[i] ?? (i === 6 ? 1 : 0);
        cur[i] = i === 6 ? (cur[i] as number) * v : (cur[i] as number) + v;
      }
      out[k] = cur;
    }
  }
  return out as unknown as P;
};

/** A wing's pose for the right side: arm, forearm and hand rotations. */
interface W { ax: number; ay: number; az: number; fy: number; fz: number; hx: number; hy: number; hz: number }

/** One wingbeat at `p` (0 = top of the stroke, wings high; loops at 1). */
const beatAt = (p: number): W => ({
  az: keys(p, [[0, 0.78], [0.42, -0.58, inOut], [1, 0.78, inOut]]),
  ay: keys(p, [[0, 0.04], [0.2, -0.12], [0.45, 0.04], [0.72, 0.26], [1, 0.04]]),
  ax: keys(p, [[0, 0], [0.2, 0.14], [0.45, 0.05], [0.72, -0.16], [1, 0]]),
  fz: keys(p, [[0, 0.1], [0.22, 0.24], [0.48, -0.16], [0.72, -0.3], [0.92, 0.04], [1, 0.1]]),
  fy: keys(p, [[0, 0], [0.42, 0.02], [0.62, -0.38], [0.8, -0.42], [1, 0]]),
  hz: keys(p, [[0, 0.14], [0.25, 0.3], [0.5, -0.22], [0.75, -0.34], [0.93, 0.12], [1, 0.14]]),
  hy: keys(p, [[0, 0], [0.42, 0.02], [0.62, 0.5], [0.8, 0.6], [1, 0]]),
  hx: keys(p, [[0, 0], [0.25, 0.08], [0.7, -0.1], [1, 0]]),
});
/** Held out to glide: a shallow V, the fingers a touch swept and the tips curling up. */
const GLIDE: W = { az: 0.06, ay: 0.08, ax: 0.04, fy: -0.04, fz: 0.04, hx: 0, hy: 0.1, hz: 0.06 };
const mix = (a: W, b: W, k: number): W => ({
  ax: lerp(a.ax, b.ax, k), ay: lerp(a.ay, b.ay, k), az: lerp(a.az, b.az, k), fy: lerp(a.fy, b.fy, k), fz: lerp(a.fz, b.fz, k),
  hx: lerp(a.hx, b.hx, k), hy: lerp(a.hy, b.hy, k), hz: lerp(a.hz, b.hz, k),
});
/** Scales a beat's swing about the glide pose (`amp` < 1 beats shallower). */
const scaled = (w: W, amp: number): W => mix(GLIDE, w, amp);
const wingR = (b: Bones<B>, w: W): void => {
  b.armR.rot(w.ax, w.ay, w.az);
  b.foreR.rot(0, w.fy, w.fz);
  b.handR.rot(w.hx, w.hy, w.hz);
};
const wingL = (b: Bones<B>, w: W): void => {
  b.armL.rot(w.ax, -w.ay, -w.az);
  b.foreL.rot(0, -w.fy, -w.fz);
  b.handL.rot(w.hx, -w.hy, -w.hz);
};
const both = (b: Bones<B>, w: W): void => {
  wingR(b, w);
  wingL(b, w);
};
/** A wing pose as key-pose channels (both sides). */
const wingPose = (w: W): P => wings([w.ax, w.ay, w.az], [0, w.fy, w.fz], [w.hx, w.hy, w.hz]);
const TOP = wingPose(beatAt(0));

/** What a beat does to the body: it heaves up on the downstroke and sags on the recovery; tail and legs trail. */
const lift = (b: Bones<B>, p: number, amp = 1): void => {
  const up = sin(TAU * (p - 0.2));
  b.root.pos(0, 0.07 * amp * up, 0);
  b.body.rot(-0.04 * amp * sin(TAU * (p - 0.05)));
  // The neck gimbals: the head stays steady while the trunk heaves.
  b.neck1.rot(0.03 * amp * sin(TAU * (p - 0.05)));
  b.head.rot(0.03 * amp * sin(TAU * (p - 0.05)));
  const tl = 0.06 * amp;
  b.tail1.rot(tl * sin(TAU * (p - 0.12)));
  b.tail2.rot(tl * sin(TAU * (p - 0.2)));
  b.tail3.rot(tl * 1.2 * sin(TAU * (p - 0.28)));
  b.tail4.rot(tl * 1.4 * sin(TAU * (p - 0.36)));
  for (const s of ['L', 'R'] as const) {
    b[`leg${s}`].rot(0.07 * amp * sin(TAU * (p - 0.3)));
    b[`shin${s}`].rot(0.1 * amp * sin(TAU * (p - 0.4)));
  }
};

/** The hovering stance: trunk reared up on the air, head held level and looking down, legs dangling, tail hanging. */
const STANCE: P = {
  body: [-0.36, 0, 0], neck1: [0.12, 0, 0], neck2: [0.2, 0, 0], head: [0.42, 0, 0], jaw: [0.06, 0, 0],
  tail1: [-0.1, 0, 0], tail2: [0.08, 0, 0], tail3: [0.12, 0, 0], tail4: [0.1, 0, 0],
  legL: [0.25, 0, 0], shinL: [0.1, 0, 0], legR: [0.25, 0, 0], shinR: [0.1, 0, 0],
};
const HOVER: P = sum(STANCE, TOP);

/** A smooth bump over a loop phase centred at `c` (cycles), `w` wide: loop-safe accents (a look, a snap). */
const bump = (p: number, c: number, w: number): number => {
  let d = Math.abs(p - c);
  d = Math.min(d, 1 - d);
  return d >= w ? 0 : smooth(1 - d / w);
};
/** A tremble: fast and small, for strain. */
const shiver = (t: number, f = 13, off = 0): number => sin(TAU * (t * f + off)) * 0.6 + sin(TAU * (t * f * 1.7 + off * 2)) * 0.4;

/** Hovering in place: two heavy beats, the body heaving, the head steady and turning to look, the tail swaying. */
const wyvernIdle = (): Clip<B> =>
  clip<B>('idle', 1.7, true, (b, t) => {
    const P = phase(t, 1.7);
    const p = phase(t, 0.85);
    const amp = 1 + 0.08 * wave(P, 0.1);
    apply(b, STANCE);
    both(b, scaled(beatAt(p), amp));
    lift(b, p, amp * 1.2);
    b.body.rot(0.03 * wave(P, 0.2));
    b.neck1.rot(0, 0.12 * wave(P, 0.3));
    b.head.rot(0.05 * bump(P, 0.6, 0.15), 0.35 * bump(P, 0.25, 0.2) - 0.3 * bump(P, 0.7, 0.18), 0.1 * bump(P, 0.25, 0.2));
    b.jaw.rot(0.22 * bump(P, 0.78, 0.07));
    // The sac glows and breathes, swelling a little with each beat.
    b.sac.scale(1 + 0.05 * wave(p, 0.1) + 0.04 * wave(P, 0.4));
    for (const [i, n] of (['tail1', 'tail2', 'tail3', 'tail4'] as const).entries()) b[n].rot(0, 0.09 * wave(P, -i * 0.11) * (1 + i * 0.2), 0);
    b.root.pos(0.03 * wave(P, 0.1), 0.03 * wave(P, 0.35), 0.02 * cwave(P, 0.2)).rot(0, 0.06 * wave(P, 0.05), 0.04 * wave(P, 0.25));
  });

/** Flying on: two hard, slow beats, then a long glide on a shallow V, sinking a little and banking; the tail streams. */
const wyvernWalk = (dur = 2.0): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const P = phase(t, dur);
    const beat = 0.56;
    if (t < beat * 2) {
      const p = phase(t, beat);
      both(b, beatAt(p));
      lift(b, p, 1.1);
    } else {
      const u = (t - beat * 2) / (dur - beat * 2);
      const k = envelope(u, 0, 0.3, 0.75, 1, inOut);
      both(b, mix(beatAt(0), GLIDE, k));
      // The heave of the last beat dies away into the glide and comes back for the next one (the loop meets).
      lift(b, 0, 1.1 * (1 - k));
      // The torn tips flutter in the air of the glide.
      b.handR.rot(0, 0, 0.05 * sin(TAU * u * 3) * k);
      b.handL.rot(0, 0, -0.05 * sin(TAU * u * 3 + 0.7) * k);
    }
    b.root.pos(0, 0.06 * cwave(P, 0.3), 0).rot(0, 0.05 * wave(P, 0.1), 0.1 * wave(P, 0.55));
    b.body.rot(0.06 + 0.03 * wave(P, 0.2));
    b.neck1.rot(-0.08, 0.06 * wave(P, 0.7));
    b.neck2.rot(0.02, 0.04 * wave(P, 0.75));
    b.head.rot(0.18, -0.06 * wave(P, 0.7), -0.06 * wave(P, 0.55));
    // The whip streams behind in a travelling S.
    for (const [i, n] of (['tail1', 'tail2', 'tail3', 'tail4'] as const).entries()) b[n].rot(0.04 + 0.04 * wave(P, -i * 0.15, 2), 0.12 * wave(P, -i * 0.14) * (1 + i * 0.25), 0.04 * wave(P, -i * 0.1));
    for (const s of ['L', 'R'] as const) {
      b[`leg${s}`].rot(0.75);
      b[`shin${s}`].rot(0.55);
    }
    b.sac.scale(1 + 0.04 * wave(P, 0.2, 2));
  });

/** The spit, from a stance already cocked: neck drawn back, sac swollen; it lunges at 0.13 s and spits, then recovers. */
const wyvernAttack = (): Clip<B> => {
  const cocked = sum(STANCE, neck(-0.5, -0.25), TOP, tail(-0.15), { body: [-0.16, 0, 0, 0, 0.04, -0.04], jaw: [0.2, 0, 0], sac: [0, 0, 0, 0, 0, 0, 1.55], legL: [-0.2, 0, 0], legR: [-0.2, 0, 0] });
  const drawn = sum(cocked, neck(-0.1, -0.05), { jaw: [0.06, 0, 0], sac: [0, 0, 0, 0, 0, 0, 1.08] });
  const spit = sum(STANCE, neck(0.42, 0.16), wingPose(beatAt(0.4)), tail(-0.12), {
    root: [0, 0, 0, 0, 0.05, -0.12], body: [0.22, 0, 0], jaw: [0.85, 0, 0], sac: [0, 0, 0, 0, 0, 0, 0.72], spit: [0, 0, 0, 0, 0, 0, JET],
    legL: [0.3, 0, 0], legR: [0.3, 0, 0],
  });
  const hold = sum(spit, neck(-0.06, 0.04), wingPose(beatAt(0.55)), wings([0, 0, 0.58]), {
    root: [0, 0, 0, 0, 0.03, -0.04], jaw: [-0.15, 0, 0], sac: [0, 0, 0, 0, 0, 0, 1.08], spit: [0, 0, 0, 0, 0, 0, 0.8],
  });
  return clip<B>('attack', 0.95, false, (b, t) => {
    timeline(b, t, [
      [0, cocked],
      [0.07, drawn, outQuad],
      [0.13, spit, inCubic],
      [0.28, hold, outQuad],
      [0.4, sum(STANCE, neck(0.25, 0.15), wingPose(beatAt(0.72)), { jaw: [0.35, 0, 0], sac: [0, 0, 0, 0, 0, 0, 0.85] }), inOut],
      [0.65, sum(STANCE, wingPose(beatAt(0.9)), { root: [0, 0, 0, 0, 0.03, 0] }), inOut],
      [0.95, HOVER, inOut],
    ], { head: 0.012, jaw: 0.006, tail2: 0.02, tail3: 0.04, tail4: 0.06, handL: 0.02, handR: 0.02 });
    // A shudder through the neck while the fire leaves it.
    const k = envelope(t, 0.12, 0.15, 0.26, 0.36);
    b.head.rot(0.03 * shiver(t) * k);
    b.jaw.rot(0.06 * shiver(t, 17) * k);
    b.spit.scale(1 + 0.12 * shiver(t, 11, 0.3) * k);
  }, (t) => {
    // It fills its chest as it draws the neck back, and the spit pushes it back.
    const k = 1 - span(t, 0.08, 0.16);
    return [1 + 0.03 * k, 1 + 0.02 * k, 1];
  });
};

/** Struck in the air: thrown back and rolled, the wings flung up out of time, the neck whipping; it rights itself. */
const wyvernHit = (): Clip<B> =>
  clip<B>('hit', 0.55, false, (b, t) => {
    timeline(b, t, [
      [0, HOVER],
      [0.06, sum(STANCE, neck(-0.55, -0.35, 0.3), tail(-0.3, 0.3), {
        root: [0, 0.15, 0.35, 0, 0.08, -0.16], body: [-0.35, 0, 0], jaw: [0.6, 0, 0], sac: [0, 0, 0, 0, 0, 0, 1.25],
        armR: [-0.2, -0.25, 1.05], foreR: [0, 0.2, 0.4], handR: [0, -0.1, 0.5], armL: [0.2, -0.35, -0.35], foreL: [0, 0.5, 0.2], handL: [0, -0.6, 0.3],
        legL: [-0.5, 0, 0], legR: [-0.3, 0, 0],
      }), outExpo],
      [0.2, sum(STANCE, neck(0.2, 0.15, -0.1), tail(0.12, -0.1), wings([0, 0.05, -0.5], [0, -0.1, -0.2], [0, 0.15, -0.25]), { root: [0, -0.05, -0.12, 0, -0.04, -0.08], body: [0.1, 0, 0], jaw: [0.25, 0, 0], sac: [0, 0, 0, 0, 0, 0, 0.9] }), inOut],
      [0.36, sum(STANCE, wingPose(beatAt(0.6)), { root: [0, 0, 0.04, 0, 0.02, -0.03] }), inOut],
      [0.55, HOVER, inOut],
    ], { head: 0.02, jaw: 0.01, tail3: 0.03, tail4: 0.05, handL: 0.025, handR: 0.02 });
  }, (t) => {
    const k = span(t, 0, 0.05, outQuad) * (1 - span(t, 0.08, 0.4, inOut));
    return [1 - 0.05 * k, 1 + 0.06 * k, 1 - 0.04 * k];
  });

/** Dazed: it lurches through the air on ragged, out-of-step beats, sagging and rolling, head lolling, jaws agape. */
const wyvernStun = (): Clip<B> =>
  clip<B>('stun', 1.8, true, (b, t) => {
    const P = phase(t, 1.8);
    apply(b, STANCE);
    // The right wing beats three times, the left twice: they never fall in step.
    wingR(b, scaled(beatAt(phase(t, 0.6)), 0.75 + 0.2 * wave(P, 0.1)));
    wingL(b, scaled(beatAt(phase(t + 0.25, 0.9)), 0.55 + 0.25 * wave(P, 0.6)));
    b.root.pos(0.06 * wave(P, 0.3), -0.25 + 0.08 * wave(P, 0, 2), 0.05 * cwave(P)).rot(0.15 * wave(P, 0.25), 0.3 * wave(P, 0.05), 0.4 * wave(P) + 0.12 * wave(P, 0.2, 3));
    b.body.rot(0.25 + 0.12 * wave(P, 0.4, 2), 0, 0.1 * wave(P, 0.7));
    b.neck1.rot(0.25 + 0.12 * wave(P, 0.1, 2), 0.25 * wave(P, 0.15));
    b.neck2.rot(0.15 + 0.1 * wave(P, 0.2, 2), 0.2 * wave(P, 0.25));
    b.head.rot(0.2 + 0.2 * wave(P, 0.15, 2), 0.3 * cwave(P, 0.15), 0.45 * wave(P, 0.15));
    b.jaw.rot(0.35 + 0.12 * wave(P, 0.3, 3));
    b.sac.scale(0.85 + 0.08 * wave(P, 0.2, 3));
    for (const [i, n] of (['tail1', 'tail2', 'tail3', 'tail4'] as const).entries()) b[n].rot(-0.12 + 0.08 * wave(P, 0.3 - i * 0.1, 2), 0.2 * wave(P, 0.3 - i * 0.12), 0.1 * wave(P, -i * 0.1));
    for (const s of ['L', 'R'] as const) {
      b[`leg${s}`].rot(-0.2 + 0.3 * wave(P, s === 'L' ? 0.35 : 0.6, 2));
      b[`shin${s}`].rot(0.3 + 0.2 * wave(P, s === 'L' ? 0.45 : 0.7, 2));
    }
  });

/** Brought down: it screams with its jaws to the sky, the wings give and fold, it falls turning and crashes on its side. */
const wyvernDie = (): Clip<B> =>
  clip<B>('die', 1.75, false, (b, t) => {
    const heap: P = sum(tail(0.12, 0.1), {
      root: [0.12, 0.35, 1.2, 0, GROUND, 0.25], body: [0.1, 0, 0], neck1: [0.5, 0.05, 0.1], neck2: [0.4, 0.05, 0.1], head: [0.3, 0.1, 0.35], jaw: [0.45, 0, 0], sac: [0, 0, 0, 0, 0, 0, 0.6],
      armR: [0.25, 0.45, -1.05], foreR: [0, -0.9, -0.35], handR: [0.1, 1.15, -0.45],
      armL: [0.1, -0.35, -1.25], foreL: [0, 0.35, 0.05], handL: [0, -0.55, 0.2],
      legL: [-0.6, 0, 0.2], shinL: [0.9, 0, 0], legR: [-0.3, 0, -0.2], shinR: [1.1, 0, 0],
    });
    const bounce: P = sum(heap, { root: [0, 0, -0.1, 0, 0.06, 0], armR: [0, 0, 0.3], handR: [0, -0.3, 0.2], neck1: [-0.15, 0, 0], tail2: [0.12, 0, 0] });
    timeline(b, t, [
      [0, HOVER],
      [0.14, sum(STANCE, neck(-0.75, -0.55), tail(-0.25), wings([0, 0.15, 1.05], [0, 0.1, 0.3], [0, -0.1, 0.35]), {
        root: [0, 0, 0, 0, 0.12, -0.05], body: [-0.3, 0, 0], jaw: [1.0, 0, 0], sac: [0, 0, 0, 0, 0, 0, 1.45], legL: [-0.4, 0, 0], legR: [-0.4, 0, 0],
      }), outCubic],
      [0.42, sum(neck(0.2, 0.2), tail(0.1, 0.15), {
        root: [0.35, 0.1, 0.45, 0, -0.18, 0.08], body: [0.2, 0, 0], jaw: [0.6, 0, 0], sac: [0, 0, 0, 0, 0, 0, 0.8],
        armR: [0, 0.5, 0.75], foreR: [0, -0.8, 0.1], handR: [0, 1.0, -0.2], armL: [0, -0.6, -0.4], foreL: [0, 0.7, 0.1], handL: [0, -0.9, 0.1],
        legL: [0.4, 0, 0], legR: [0.2, 0, 0],
      }), inOut],
      [0.82, sum(heap, { root: [0.4, 0.25, 0.9, 0, GROUND + 0.02, 0.22], armR: [0, 0, 0.5], neck1: [-0.2, 0, 0], neck2: [-0.15, 0, 0] }), inQuad],
      [0.95, bounce, outQuad],
      [1.12, heap, inQuad],
      [1.75, heap, outCubic],
    ], { neck2: 0.02, head: 0.04, jaw: 0.02, tail2: 0.02, tail3: 0.04, tail4: 0.06, handL: 0.04, handR: 0.05, armR: 0.02 });
    // A last spasm of the scream, then the sac dims.
    const k = envelope(t, 0.1, 0.16, 0.3, 0.4);
    b.head.rot(0.05 * shiver(t, 12) * k);
    b.jaw.rot(0.08 * shiver(t, 17) * k);
  }, (t) => {
    // The crash flattens it for a beat.
    const k = Math.max(0, settle(t - 0.82, 3, 9)) * span(t, 0.81, 0.83);
    return [1 + 0.08 * k, 1 - 0.12 * k, 1 + 0.06 * k];
  });

/**
 * The longer spit: it rears and fills the sac until it glows, lunges and holds a jet of fire on the tower,
 * sweeping it across while the wings beat short and hard to hold it in the air, then lets go.
 */
const wyvernBreath = (): Clip<B> => {
  const draw = sum(STANCE, neck(-0.65, -0.3), tail(-0.1), { root: [0, 0, 0, 0, 0.06, -0.06], body: [-0.08, 0, 0], jaw: [0.25, 0, 0], sac: [0, 0, 0, 0, 0, 0, 1.75], legL: [-0.3, 0, 0], legR: [-0.3, 0, 0] });
  const jet = sum(STANCE, neck(0.4, 0.16), tail(-0.1), {
    root: [0, 0, 0, 0, 0.02, -0.12], body: [0.2, 0, 0], jaw: [0.95, 0, 0], sac: [0, 0, 0, 0, 0, 0, 1.15], spit: [0, 0, 0, 0, 0, 0, JET * 1.15],
    legL: [0.3, 0, 0], legR: [0.3, 0, 0],
  });
  const spent = sum(jet, neck(-0.1, -0.05), { jaw: [-0.15, 0, 0], sac: [0, 0, 0, 0, 0, 0, 0.62], spit: [0, 0, 0, 0, 0, 0, 0.85] });
  return clip<B>('breath', 1.2, false, (b, t) => {
    timeline(b, t, [
      [0, STANCE],
      [0.26, draw, inOut],
      [0.34, jet, inCubic],
      [0.86, spent, (k) => k],
      [0.96, sum(STANCE, neck(0.2, 0.1), { jaw: [0.3, 0, 0], sac: [0, 0, 0, 0, 0, 0, 0.8] }), outQuad],
      [1.2, STANCE, inOut],
    ], { head: 0.015, jaw: 0.008, tail3: 0.03, tail4: 0.05 });
    // The wings: two beats, short and hard while it sprays.
    const hold = envelope(t, 0.3, 0.4, 0.86, 1.0);
    const p = phase(t, 0.6);
    both(b, scaled(beatAt(p), 1 - 0.35 * hold));
    lift(b, p, 1 - 0.4 * hold);
    // The neck sweeps the jet across the tower and everything trembles with the strain.
    const sweep = keys(t, [[0.34, -0.22], [0.86, 0.22, inOut]]);
    b.neck1.rot(0, sweep * hold * 0.6);
    b.neck2.rot(0, sweep * hold * 0.5);
    b.head.rot(0.025 * shiver(t) * hold, -sweep * hold * 0.3, 0.02 * shiver(t, 9) * hold);
    b.jaw.rot(0.05 * shiver(t, 16) * hold);
    b.spit.scale(1 + 0.14 * shiver(t, 10, 0.2) * hold);
    b.sac.scale(1 + 0.05 * shiver(t, 7) * hold);
  }, (t) => {
    const k = envelope(t, 0.05, 0.26, 0.28, 0.4);
    return [1 + 0.035 * k, 1 + 0.02 * k, 1];
  }, 'Escupir fuego');
};

export const wyvernClips = (): Clip[] => [wyvernIdle(), wyvernWalk(), wyvernAttack(), wyvernHit(), wyvernStun(), wyvernDie(), wyvernBreath()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { wyvern: wyvernClips };
